// We discriminate the `node` prop before applying the remaining props to the
// resulting tag. Therefore, `node` is always defined, but never used.

import { Heading, Text } from "@radix-ui/themes";
import cx from "classnames";
import React from "react";
import { type FC, type PropsWithChildren } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import rehypeColorChip from "../plugins/rehype-color-chip";
import rehypeSuperscript from "../plugins/rehype-superscript";

const headingClass = "mb-2 mt-4 first:mt-0";
const tableWrapperClass =
  "my-3 overflow-x-auto rounded-lg border border-puntt-neutral-gray-6";

// Sentinel uses Unicode private-use-area characters (U+E000, U+E001)
// that have no legitimate meaning in user-authored text, so a real
// input cannot collide with the placeholder during code-fence extraction.
const CODE_FENCE_OPEN = "";
const CODE_FENCE_CLOSE = "";
const CODE_FENCE_PLACEHOLDER = /(\d+)/g;

/**
 * Normalize the raw markdown string before rendering.
 *
 * Collapses excessive newlines and merges paragraphs that were
 * artificially split between sentence fragments. The LLM backing
 * ask-puntt periodically emits `\n\n` between a word and the comma,
 * period, or em-dash that should follow it, producing a fragmented
 * stack of one-word paragraphs at render time. This helper stitches
 * those orphans back into their parent paragraph.
 *
 * Fenced code blocks are extracted first so the merging rules never
 * touch lines like `.gitignore` or `.env` inside ```...``` regions.
 */
function normalizeNewlines(s: string): string {
  // Extract fenced code blocks so the merging rules never touch them.
  const codes: string[] = [];
  const protectedInput = s.replace(/```[\s\S]*?```/g, (m) => {
    codes.push(m);
    return `${CODE_FENCE_OPEN}${codes.length - 1}${CODE_FENCE_CLOSE}`;
  });

  let out = protectedInput.replace(/\n{3,}/g, "\n\n");
  // Merge paragraphs starting with sentence-final closing punctuation
  // (`,` `.` `;` `:` `!` `?` `)` `]`). The punctuation belongs flush
  // against the word that ended the previous paragraph.
  out = out.replace(/\n\n([.,;:!?)\]])/g, "$1");
  // Narrow merge for orphan quotes: only when the quote is followed
  // by sentence-final punctuation or a lowercase letter (the orphan
  // continuation case). Legitimate paragraphs that start with a real
  // quote like `"This is a quote..."` are preserved.
  out = out.replace(/\n\n(["'])(\s*[.,;:!?a-z])/g, "$1$2");
  // Merge paragraphs that begin with an em-dash or en-dash. Reassembly
  // uses single-space separators so dash typography reads correctly.
  out = out.replace(/\n\n([—–])\s/g, " $1 ");

  return out.replace(CODE_FENCE_PLACEHOLDER, (_, i) => codes[Number(i)]);
}

function isWhitespaceOnly(children: React.ReactNode): boolean {
  if (children == null) return true;
  const s =
    typeof children === "string"
      ? children
      : Array.isArray(children)
        ? children.map((c) => (typeof c === "string" ? c : "")).join("")
        : String(children);
  return /^\s*$/.test(s);
}

export const Markdown: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[rehypeRaw, rehypeColorChip, rehypeSuperscript]}
    className={cx(
      // `break-words` (overflow-wrap: break-word) breaks genuinely
      // unbreakable strings like long URLs as a last resort WITHOUT
      // reducing min-content intrinsic size. The legacy
      // `word-break: break-word` we used before behaves like
      // `overflow-wrap: anywhere`, which DOES count intra-word break
      // points in min-content sizing — that collapsed auto-layout
      // table columns to ~one character and broke ordinary words
      // mid-character ("Regulator" -> "Regulat"/"or"). Since these
      // properties inherit, fixing the container covers tables, the
      // canvas, and every other nested markdown surface.
      "whitespace-normal break-words [&_p:empty]:hidden [&_p:last-child]:mb-0 [&_p]:mb-2",
      className,
    )}
    components={{
      h1: ({ node, ...props }) => (
        <Heading
          as="h1"
          size="6"
          className={headingClass}
          {...props}
          color={undefined}
        />
      ),
      h2: ({ node, ...props }) => (
        <Heading
          as="h2"
          size="5"
          className={headingClass}
          {...props}
          color={undefined}
        />
      ),
      h3: ({ node, ...props }) => (
        <Heading
          as="h3"
          size="4"
          className={headingClass}
          {...props}
          color={undefined}
        />
      ),
      h4: ({ node, ...props }) => (
        <Heading
          as="h4"
          size="3"
          className={headingClass}
          {...props}
          color={undefined}
        />
      ),
      h5: ({ node, ...props }) => (
        <Heading
          as="h5"
          size="2"
          className={headingClass}
          {...props}
          color={undefined}
        />
      ),
      h6: ({ node, ...props }) => (
        <Heading
          as="h6"
          size="1"
          className={headingClass}
          {...props}
          color={undefined}
        />
      ),
      strong: ({ node, ...props }) => (
        <Text as="span" weight="bold" {...props} color={undefined} />
      ),
      em: ({ node, ...props }) => (
        <Text as="span" className="italic" {...props} color={undefined} />
      ),
      //@ts-expect-error TS2322: different default prop types, but this is fully compatible
      del: ({ node, ...props }) => <Text {...props} />,
      p: ({ node, children, ...props }) => {
        if (isWhitespaceOnly(children)) return null;
        return (
          <Text as="p" size="3" {...props} color={undefined}>
            {children}
          </Text>
        );
      },
      li: ({ node, ...props }) => (
        <li>
          <Text as="span" {...props} color={undefined} />
        </li>
      ),
      ul: ({ node, ...props }) => (
        <ul className="list-disc whitespace-normal pl-4 text-base" {...props} />
      ),
      ol: ({ node, ...props }) => (
        <ol
          className="list-decimal whitespace-normal pl-5 text-base"
          {...props}
        />
      ),
      a: ({ node, ...props }) => (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a className="puntt-link underline" target="_blank" {...props} />
      ),
      pre: ({ node, ...props }) => (
        <pre className="max-w-56 overflow-auto" {...props} />
      ),
      code: ({ node, ...props }) => (
        <Text size="3" {...props} color={undefined} />
      ),
      hr: ({ node, ...props }) => (
        <hr className="my-4 border-carbon-300" {...props} />
      ),
      blockquote: ({ node, ...props }) => (
        <blockquote
          className="whitespace-wrap rounded-r-lg border-l-2 border-l-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 py-3 pl-6"
          {...props}
        />
      ),
      table: ({ node, ...props }) => (
        <div className={tableWrapperClass}>
          <table
            className="w-full border-collapse text-left text-sm"
            {...props}
          />
        </div>
      ),
      thead: ({ node, ...props }) => (
        <thead className="bg-puntt-neutral-gray-3" {...props} />
      ),
      tbody: ({ node, ...props }) => <tbody {...props} />,
      tr: ({ node, ...props }) => (
        <tr
          className="border-b border-puntt-neutral-gray-6 last:border-b-0"
          {...props}
        />
      ),
      th: ({ node, ...props }) => (
        <th
          className="px-3 py-2 font-semibold text-puntt-neutral-gray-12"
          {...props}
        />
      ),
      td: ({ node, ...props }) => (
        <td
          className="px-3 py-2 align-top text-puntt-neutral-gray-12"
          {...props}
        />
      ),
    }}
  >
    {normalizeNewlines(children as string)}
  </ReactMarkdown>
);
